import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";

/**
 * Manually-imported expense invoices (FedEx/UPS/Amazon/ads feeds) and the two
 * derived read models: shipping lines and PPC sales. Wire names match the
 * Java `@JsonProperty`s. `invoice_currency` is the SIGM currency id
 * (0 = CAD, 1 = USD, -1 = ALL), not the enum name.
 */

export const INVOICE_EXPENSE_SUPPLIERS = [
  "FEDEX",
  "FEDEX_TRADE",
  "FASTEX",
  "UPS",
  "AMAZON",
  "FACEBOOK",
  "GOOGLE",
  "OVERSTOCK",
  "WAYFAIR",
] as const;
export type InvoiceExpenseSupplier = (typeof INVOICE_EXPENSE_SUPPLIERS)[number];

/**
 * The server's import switch only handles these; FASTEX and UPS fall through
 * to an empty result while claiming success, so the import form offers only
 * the suppliers that actually parse.
 */
export const IMPORTABLE_SUPPLIERS: InvoiceExpenseSupplier[] = [
  "FEDEX",
  "FEDEX_TRADE",
  "AMAZON",
  "FACEBOOK",
  "GOOGLE",
  "OVERSTOCK",
  "WAYFAIR",
];

export const INVOICE_EXPENSE_CATEGORIES = [
  "SHIPPING",
  "LINEHAUL",
  "MARKETING_ADS",
  "MARKETING_AGENCY",
] as const;

export interface ImportInvoiceDTO {
  id: number;
  invoice_no?: string | null;
  invoice_date?: string | null;
  invoice_currency?: number;
  invoice_supplier?: InvoiceExpenseSupplier | string | null;
  invoice_expense_category?: (typeof INVOICE_EXPENSE_CATEGORIES)[number] | string | null;
  client_id?: number | null;
  subtotal?: number;
  tax_1?: number;
  tax_2?: number;
}

export interface ShippingLineDTO {
  id: number;
  invoice_id?: number;
  invoice_no?: string | null;
  invoice_date?: string | null;
  invoice_currency?: number;
  invoice_client_id?: number;
  shipping_invoice_no?: string | null;
  shipping_invoice_line_no?: string | null;
  shipping_invoice_date?: string | null;
  shipping_invoice_currency?: number;
  ship_date?: string | null;
  recipient_name?: string | null;
  recipient_address_1?: string | null;
  recipient_address_2?: string | null;
  recipient_address_city?: string | null;
  recipient_address_state?: string | null;
  recipient_address_postal_code?: string | null;
  recipient_address_country?: string | null;
  subtotal?: number;
  tax_1?: number;
  tax_2?: number;
  invoice?: ImportInvoiceDTO | null;
}

export interface PpcSalesDTO {
  id: number;
  invoice_no?: string | null;
  sales_date?: string | null;
  client_id?: number;
  client_no?: string | null;
  client_name?: string | null;
  client_currency?: number;
  sales?: number;
  invoice?: ImportInvoiceDTO | null;
}

export class ImportInvoiceClient extends ResourceClient<ImportInvoiceDTO> {
  constructor(http: HttpClient) {
    super(http, "/manualinvoice/importinvoice");
  }

  /**
   * Raw file bytes, supplier as a query param — no multipart, no filename.
   * The response is a list of row-level error messages; empty means success.
   */
  importFeed(supplier: InvoiceExpenseSupplier, file: File): Promise<string[]> {
    return this.http.request<string[]>(
      `/manualinvoice/importinvoice/add?expense_supplier=${supplier}`,
      { method: "POST", raw: file },
    );
  }
}

export class ShippingLineClient extends ResourceClient<ShippingLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/manualinvoice/shippingline");
  }
}

export class PpcSalesClient extends ResourceClient<PpcSalesDTO> {
  constructor(http: HttpClient) {
    super(http, "/manualinvoice/ppcsales");
  }
}
